import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Video as VideoIcon, ListVideo, Save, X, Pencil } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import TranslateButton from './TranslateButton';
import { FreeToggle } from './FreeToggle';
import { RichTextEditor } from './RichTextEditor';
import { AttachmentsManager } from './course-editor/AttachmentsManager';

const emptyForm = {
  titleAr: '', titleEn: '', youtube: '', duration_min: 0, order: 0,
  videoFile: null, videoFileName: '', is_free: false,
  section_id: '', descriptionAr: '', descriptionEn: '', learnArText: '', learnEnText: '', outlineArText: '', outlineEnText: '', quiz_id: '',
};
const lines = (t) => t.split('\n').map(x => x.trim());

/**
 * Manage lessons for a course. Renders a list of lessons + an add/edit form.
 * Calls /api/courses/{course_id}/lessons endpoints.
 *
 * Props:
 *   courseId: string
 *   lessons:  Array<{ id, titleAr, titleEn, youtube, video_url, duration_min, order }>
 *   onChange: (newLessons) => void
 */
const LessonsManager = ({ courseId, lessons, onChange, sections = [] }) => {
  const { lang, isRTL } = useLang();
  const { toast } = useToast();
  const L = (ar, en) => tx(lang, ar, en);
  const [form, setForm] = useState(emptyForm);
  const [quizzes, setQuizzes] = useState([]);
  useEffect(() => { api.get('/admin/quizzes').then(({ data }) => setQuizzes(data || [])).catch(() => {}); }, []);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setForm(emptyForm); setEditingId(null); };

  const toggleLessonFree = async (lesson) => {
    const next = !lesson.is_free;
    onChange(lessons.map(l => (l.id === lesson.id ? { ...l, is_free: next } : l)));
    try {
      const fd = new FormData();
      fd.append('is_free', next ? 'true' : 'false');
      await api.patch(`/courses/${courseId}/lessons/${lesson.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: next ? L('الدرس أصبح مجانياً', 'Lesson is now free') : L('الدرس أصبح مدفوعاً', 'Lesson is now paid') });
    } catch (e) {
      onChange(lessons.map(l => (l.id === lesson.id ? { ...l, is_free: !next } : l)));
      toast({ title: 'Error', description: e?.response?.data?.detail || L('تعذر الحفظ', 'Could not save') });
    }
  };

  const startEdit = (lesson) => {
    setEditingId(lesson.id);
    setForm({
      titleAr: lesson.titleAr || '',
      titleEn: lesson.titleEn || '',
      youtube: lesson.youtube || '',
      duration_min: lesson.duration_min || 0,
      order: lesson.order || 0,
      videoFile: null, videoFileName: '',
      is_free: !!lesson.is_free,
      section_id: lesson.section_id || '',
      descriptionAr: lesson.descriptionAr || '', descriptionEn: lesson.descriptionEn || '',
      learnArText: (lesson.learnAr || []).join('\n'), learnEnText: (lesson.learnEn || []).join('\n'),
      outlineArText: (lesson.outlineAr || []).join('\n'), outlineEnText: (lesson.outlineEn || []).join('\n'),
      quiz_id: lesson.quiz_id || '',
    });
  };

  const onVideoFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setForm({ ...form, videoFile: f, videoFileName: f.name });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titleAr.trim()) {
      toast({ title: L('املأ العناوين بالعربية والإنجليزية', 'Fill both Arabic and English titles') });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('titleAr', form.titleAr);
      fd.append('titleEn', form.titleEn);
      fd.append('youtube', form.youtube || '');
      fd.append('duration_min', String(form.duration_min || 0));
      fd.append('order', String(form.order || 0));
      fd.append('is_free', form.is_free ? 'true' : 'false');
      fd.append('section_id', form.section_id || '');
      fd.append('descriptionAr', form.descriptionAr || '');
      fd.append('descriptionEn', form.descriptionEn || '');
      fd.append('learnAr', JSON.stringify(lines(form.learnArText)));
      fd.append('learnEn', JSON.stringify(lines(form.learnEnText)));
      fd.append('quiz_id', form.quiz_id || '');
      fd.append('outlineAr', JSON.stringify(lines(form.outlineArText).filter(Boolean)));
      fd.append('outlineEn', JSON.stringify(lines(form.outlineEnText).filter(Boolean)));
      if (form.videoFile) fd.append('video', form.videoFile);

      let updated;
      if (editingId) {
        const { data } = await api.patch(`/courses/${courseId}/lessons/${editingId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        updated = lessons.map(l => l.id === editingId ? data : l);
        toast({ title: L('تم تحديث الدرس', 'Lesson updated') });
      } else {
        const { data } = await api.post(`/courses/${courseId}/lessons`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        updated = [...lessons, data];
        toast({ title: L('تمت إضافة الدرس', 'Lesson added') });
      }
      onChange?.(updated);
      reset();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (lessonId) => {
    if (!window.confirm(L('حذف هذا الدرس؟', 'Delete this lesson?'))) return;
    try {
      await api.delete(`/courses/${courseId}/lessons/${lessonId}`);
      onChange?.(lessons.filter(l => l.id !== lessonId));
      toast({ title: L('تم الحذف', 'Deleted') });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete' });
    }
  };

  return (
    <div className="space-y-4 rounded-xl bg-slate-50 border border-slate-200 p-4">
      <div className={`flex items-center gap-2`}>
        <ListVideo className="w-5 h-5 text-blue-600" />
        <h4 className="text-slate-900 font-bold">{L('دروس الكورس', 'Course Lessons')}</h4>
        <span className="text-xs text-slate-500">({lessons.length})</span>
      </div>

      {/* Existing lessons */}
      <div className="space-y-2">
        {lessons.length === 0 && (
          <div className="text-slate-500 text-sm text-center py-3">
            {L('لم تتم إضافة دروس بعد', 'No lessons added yet')}
          </div>
        )}
        {lessons.map((l, i) => (
          <div key={l.id} className={`flex items-center gap-3 rounded-lg bg-white border border-slate-200 p-3`} data-testid={`lesson-row-${i}`}>
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center font-bold text-sm shrink-0">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-900 text-sm font-semibold truncate">{l.titleAr || '—'}</div>
              <div className="text-slate-500 text-xs truncate" dir="ltr">{l.titleEn || '—'}</div>
              {sections.find(s => s.id === l.section_id) && <div className="text-[11px] text-blue-600 font-bold">{sections.find(s => s.id === l.section_id).titleAr}</div>}
              <div className="text-[11px] text-slate-500 mt-0.5" dir="ltr">
                {l.duration_min ? `${l.duration_min} min` : ''}
                {l.video_url ? ' · video' : (l.youtube ? ' · YouTube' : '')}
              </div>
            </div>
            <FreeToggle value={l.is_free} onChange={() => toggleLessonFree(l)} testId={`lesson-free-toggle-${i}`} />
            <Button type="button" data-testid={`lesson-edit-${i}`} onClick={() => startEdit(l)} variant="outline" size="sm" className="border-blue-200 text-blue-700 hover:bg-slate-100 h-8 px-2"><Pencil className="w-3.5 h-3.5" /></Button>
            <Button type="button" data-testid={`lesson-delete-${i}`} onClick={() => remove(l.id)} variant="outline" size="sm" className="border-red-500/40 text-red-600 hover:bg-red-500/10 h-8 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </div>

      {/* Add / edit form */}
      <div className="rounded-lg bg-white border-2 border-dashed border-blue-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h5 className="text-blue-700 text-sm font-bold">
            {editingId ? L('تعديل الدرس', 'Edit lesson') : L('إضافة درس جديد', 'Add new lesson')}
          </h5>
          {editingId && (
            <Button type="button" onClick={reset} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900 h-7 px-2"><X className="w-3.5 h-3.5" /></Button>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          <Input data-testid="lesson-title-ar" value={form.titleAr} onChange={e => setForm({ ...form, titleAr: e.target.value })} placeholder={L('عنوان الدرس بالعربية', 'Lesson title (Arabic)')} className="bg-slate-50 border-slate-200 text-slate-900 h-10 text-sm" />
          <Input data-testid="lesson-title-en" value={form.titleEn} onChange={e => setForm({ ...form, titleEn: e.target.value })} placeholder={L('عنوان الدرس بالإنجليزية', 'Lesson title (English)')} className="bg-slate-50 border-slate-200 text-slate-900 h-10 text-sm" dir="ltr" />
        </div>

        <div className="flex flex-wrap gap-2">
          <TranslateButton
            sourceLang="ar"
            targetLang="en"
            getValues={() => form}
            setValue={(k, v) => setForm(prev => ({ ...prev, [k]: v }))}
            fields={[{ source: 'titleAr', target: 'titleEn' }, { source: 'descriptionAr', target: 'descriptionEn' }, { source: 'learnArText', target: 'learnEnText' }, { source: 'outlineArText', target: 'outlineEnText' }]}
            testId="lesson-translate-ar-en"
          />
          <TranslateButton
            sourceLang="en"
            targetLang="ar"
            getValues={() => form}
            setValue={(k, v) => setForm(prev => ({ ...prev, [k]: v }))}
            fields={[{ source: 'titleEn', target: 'titleAr' }]}
            testId="lesson-translate-en-ar"
          />
        </div>

        <div className="grid gap-2">
          <div>
            <label className="text-slate-500 text-[11px] mb-1 block">{L('رابط YouTube (اختياري)', 'YouTube URL (optional)')}</label>
            <Input data-testid="lesson-youtube" value={form.youtube} onChange={e => setForm({ ...form, youtube: e.target.value })} placeholder="https://youtu.be/..." className="bg-slate-50 border-slate-200 text-slate-900 h-10 text-sm" dir="ltr" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <label className="text-slate-500 text-[11px] mb-1 block">{L('القسم', 'Section')}</label>
            <select data-testid="lesson-section" value={form.section_id} onChange={e => setForm({ ...form, section_id: e.target.value })} className="w-full h-10 rounded-md bg-slate-50 border border-slate-200 px-3 text-sm">
              <option value="">{L('بدون قسم', 'No section')}</option>
              {sections.map(s => <option key={s.id} value={s.id}>{s.titleAr}</option>)}
            </select>
          </div>
          <div>
            <label className="text-slate-500 text-[11px] mb-1 block">{L('اختبار الدرس (اختياري)', 'Lesson quiz (optional)')}</label>
            <select data-testid="lesson-quiz" value={form.quiz_id} onChange={e => setForm({ ...form, quiz_id: e.target.value })} className="w-full h-10 rounded-md bg-slate-50 border border-slate-200 px-3 text-sm">
              <option value="">{L('بدون اختبار', 'No quiz')}</option>
              {quizzes.map(q => <option key={q.id} value={q.id}>{q.titleAr}</option>)}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <label className="text-slate-500 text-[11px] mb-1 block">{L('وصف الدرس (عربي)', 'Lesson description (Arabic)')}</label>
            <RichTextEditor key={`ar-${editingId || 'new'}`} testId="lesson-desc-ar" dir="rtl" value={form.descriptionAr} onChange={v => setForm(f => ({ ...f, descriptionAr: v }))} placeholder={L('حول هذا الدرس...', 'About this lesson...')} />
          </div>
          <div>
            <label className="text-slate-500 text-[11px] mb-1 block">{L('وصف الدرس (إنجليزي)', 'Lesson description (English)')}</label>
            <RichTextEditor key={`en-${editingId || 'new'}`} testId="lesson-desc-en" dir="ltr" value={form.descriptionEn} onChange={v => setForm(f => ({ ...f, descriptionEn: v }))} placeholder="About this lesson..." />
          </div>
          <div>
            <label className="text-slate-500 text-[11px] mb-1 block">{L('في هذا الدرس ستتعلم (نقطة في كل سطر)', 'You will learn (one point per line)')}</label>
            <textarea data-testid="lesson-learn-ar" rows={3} dir="rtl" value={form.learnArText} onChange={e => setForm({ ...form, learnArText: e.target.value })} className="w-full rounded-md bg-slate-50 border border-slate-200 p-2 text-sm" />
          </div>
          <div>
            <label className="text-slate-500 text-[11px] mb-1 block">{L('النقاط بالإنجليزية (نفس الترتيب)', 'Points in English (same order)')}</label>
            <textarea data-testid="lesson-learn-en" rows={3} dir="ltr" value={form.learnEnText} onChange={e => setForm({ ...form, learnEnText: e.target.value })} className="w-full rounded-md bg-slate-50 border border-slate-200 p-2 text-sm" />
          </div>
          <div>
            <label className="text-slate-500 text-[11px] mb-1 block">{L('محتوى الدرس (سطر لكل عنوان، مثال: مقدمة الدرس | 02:15)', 'Lesson outline (one per line, e.g. Intro | 02:15)')}</label>
            <textarea data-testid="lesson-outline-ar" rows={3} dir="rtl" value={form.outlineArText} onChange={e => setForm({ ...form, outlineArText: e.target.value })} className="w-full rounded-md bg-slate-50 border border-slate-200 p-2 text-sm" />
          </div>
          <div>
            <label className="text-slate-500 text-[11px] mb-1 block">{L('محتوى الدرس بالإنجليزية (نفس الترتيب)', 'Outline in English (same order)')}</label>
            <textarea data-testid="lesson-outline-en" rows={3} dir="ltr" value={form.outlineEnText} onChange={e => setForm({ ...form, outlineEnText: e.target.value })} className="w-full rounded-md bg-slate-50 border border-slate-200 p-2 text-sm" />
          </div>
        </div>

        {editingId && (
          <AttachmentsManager courseId={courseId} lessonId={editingId}
            attachments={(lessons.find(l => l.id === editingId) || {}).attachments || []}
            onChange={atts => onChange(lessons.map(l => (l.id === editingId ? { ...l, attachments: atts } : l)))} />
        )}

        <div className="flex items-center gap-3">
          <span className="text-slate-600 text-xs">{L('حالة الدرس:', 'Lesson access:')}</span>
          <FreeToggle value={form.is_free} onChange={(val) => setForm({ ...form, is_free: val })} testId="lesson-form-free-toggle" />
        </div>

        {/* Video upload */}
        <label className="block">
          <div className="rounded-lg border-2 border-dashed border-slate-200 hover:border-blue-300 transition-colors p-3 text-center cursor-pointer bg-slate-50">
            <VideoIcon className="w-5 h-5 text-blue-600 mx-auto mb-1" />
            <div className="text-slate-600 text-xs">{form.videoFileName || L('اضغط لرفع فيديو الدرس (اختياري)', 'Upload lesson video (optional)')}</div>
            <input type="file" accept="video/*" onChange={onVideoFile} className="hidden" data-testid="lesson-video-file" />
          </div>
        </label>

        <div>
          <label className="text-slate-500 text-[11px] mb-1 block">{L('الترتيب', 'Order')}</label>
          <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-slate-50 border-slate-200 text-slate-900 h-10 text-sm w-32" />
        </div>

        <Button type="button" onClick={submit} disabled={busy} data-testid="save-lesson-btn" className="btn-primary-blue border-0 h-10 px-5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : (editingId ? <Save className="w-4 h-4 me-2" /> : <Plus className="w-4 h-4 me-2" />)}
          {editingId ? L('حفظ التعديلات', 'Save changes') : L('إضافة الدرس', 'Add lesson')}
        </Button>
      </div>
    </div>
  );
};

export default LessonsManager;
